import React, { useEffect, useRef, useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MIN_SIZE = 3, MAX_SIZE = 10;
const MODES = [
  { key: 'build', label: 'Build' },
  { key: 'traverse', label: 'Traverse' },
  { key: 'reverse', label: 'Reverse' },
  { key: 'search', label: 'Linear Search' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const emptyArray = n => Array.from({ length: n }, () => null);

/* ─── Java code shown alongside the animation, per mode/step ───────── */
const codeFor = (mode, ctx) => {
  if (mode === 'build') {
    if (ctx.action === 'create') return { lines: [`int[] arr = new int[${ctx.size}];`, '// every slot defaults to 0'], active: 0 };
    if (ctx.action === 'insert') return { lines: [`arr[${ctx.index}] = ${ctx.value};`], active: 0 };
    if (ctx.action === 'click') return { lines: [`System.out.println(arr[${ctx.index}]);`, `// -> ${ctx.value === null ? 'not set yet' : ctx.value}`], active: 0 };
    if (ctx.action === 'update') return { lines: [`arr[${ctx.index}] = ${ctx.value}; // was ${ctx.prev}`], active: 0 };
    return { lines: ['int[] arr = new int[size];', '// create an array, then insert values or click a box'], active: -1 };
  }
  if (mode === 'traverse') {
    return {
      lines: [
        'for (int i = 0; i < arr.length; i++) {',
        '    System.out.println(arr[i]);',
        '}',
      ],
      active: ctx.started ? 1 : 0,
    };
  }
  if (mode === 'reverse') {
    return {
      lines: [
        'int left = 0, right = arr.length - 1;',
        'while (left < right) {',
        '    int temp = arr[left];',
        '    arr[left] = arr[right];',
        '    arr[right] = temp;',
        '    left++; right--;',
        '}',
      ],
      active: ctx.line,
    };
  }
  if (mode === 'search') {
    return {
      lines: [
        'for (int i = 0; i < arr.length; i++) {',
        '    if (arr[i] == target) {',
        '        return i;',
        '    }',
        '}',
        'return -1;',
      ],
      active: ctx.line,
    };
  }
  return { lines: [], active: -1 };
};

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Array box row ──────────────────────────────────────────────── */
const BoxRow = ({ array, getBoxState, onClickBox }) => (
  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingBottom: 22 }}>
    {array.map((val, i) => {
      const st = getBoxState ? getBoxState(i) : {};
      const bg = st.bg || 'var(--bg3)';
      const border = st.border || 'var(--border)';
      const color = st.color || 'var(--text)';
      return (
        <div key={i} style={{ position: 'relative' }}>
          <button
            onClick={() => onClickBox && onClickBox(i)}
            disabled={!onClickBox}
            style={{
              width: 50, height: 50, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 800, fontFamily: "'Inter',sans-serif",
              background: bg, color, border: `1.5px solid ${border}`,
              cursor: onClickBox ? 'pointer' : 'default', transition: 'all 0.2s ease',
              transform: st.pop ? 'scale(1.12)' : 'scale(1)',
            }}
          >
            {val === null ? '·' : val}
          </button>
          <span style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', bottom: -18, fontSize: 10, fontWeight: 700, color: 'var(--muted)' }}>{i}</span>
          {st.tag && (
            <span style={{
              position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)',
              fontSize: 9.5, fontWeight: 800, color: st.tagColor || V1, whiteSpace: 'nowrap',
            }}>{st.tag}</span>
          )}
        </div>
      );
    })}
  </div>
);

const smallInput = {
  width: 78, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});

/* ─── Main playground ────────────────────────────────────────────── */
const ArrayPlayground = () => {
  const [size, setSize] = useState(6);
  const [sizeInput, setSizeInput] = useState('6');
  const [array, setArray] = useState(emptyArray(6));
  const [mode, setMode] = useState('build');

  // Build panel state
  const [insertValue, setInsertValue] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [updateIndex, setUpdateIndex] = useState('');
  const [updateValue, setUpdateValue] = useState('');
  const [flashIndex, setFlashIndex] = useState(null);
  const [lastAction, setLastAction] = useState({ action: null });

  // Traverse state
  const [traverseStep, setTraverseStep] = useState(-1);
  const [traversePlaying, setTraversePlaying] = useState(false);

  // Reverse state
  const [reverseSteps, setReverseSteps] = useState(null);
  const [reverseStep, setReverseStep] = useState(0);

  // Search state
  const [searchTarget, setSearchTarget] = useState('');
  const [searchSteps, setSearchSteps] = useState(null);
  const [searchStep, setSearchStep] = useState(0);

  // Quiz state
  const [quizIndex, setQuizIndex] = useState(null);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [quizResult, setQuizResult] = useState(null); // null | 'correct' | 'wrong'
  const [quizScore, setQuizScore] = useState({ correct: 0, total: 0 });

  const flashTimer = useRef(null);
  const filledCount = array.filter(v => v !== null).length;
  const isFull = filledCount === array.length;

  /* create */
  const createArray = () => {
    const n = Math.min(MAX_SIZE, Math.max(MIN_SIZE, parseInt(sizeInput, 10) || MIN_SIZE));
    setSize(n);
    setArray(emptyArray(n));
    setSelectedIndex(null);
    setLastAction({ action: 'create', size: n });
    resetOperationState();
  };

  const resetOperationState = () => {
    setMode('build');
    setTraverseStep(-1); setTraversePlaying(false);
    setReverseSteps(null); setReverseStep(0);
    setSearchSteps(null); setSearchStep(0); setSearchTarget('');
    setQuizIndex(null); setQuizResult(null); setQuizAnswer('');
  };

  /* insert */
  const insertNext = () => {
    const idx = array.findIndex(v => v === null);
    if (idx === -1 || insertValue.trim() === '' || isNaN(Number(insertValue))) return;
    const v = Number(insertValue);
    const next = [...array]; next[idx] = v; setArray(next);
    setInsertValue('');
    setFlash(idx);
    setLastAction({ action: 'insert', index: idx, value: v });
  };

  const setFlash = idx => {
    setFlashIndex(idx);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashIndex(null), 650);
  };

  const clickBox = i => {
    setSelectedIndex(i);
    setLastAction({ action: 'click', index: i, value: array[i] });
  };

  const applyUpdate = () => {
    const idx = parseInt(updateIndex, 10);
    if (isNaN(idx) || idx < 0 || idx >= array.length || updateValue.trim() === '' || isNaN(Number(updateValue))) return;
    const prev = array[idx];
    const next = [...array]; next[idx] = Number(updateValue); setArray(next);
    setFlash(idx);
    setLastAction({ action: 'update', index: idx, value: Number(updateValue), prev: prev === null ? '·' : prev });
    setUpdateValue('');
  };

  /* traverse autoplay */
  useEffect(() => {
    if (!traversePlaying) return;
    if (traverseStep >= array.length - 1) { setTraversePlaying(false); return; }
    const t = setTimeout(() => setTraverseStep(s => s + 1), 850);
    return () => clearTimeout(t);
  }, [traversePlaying, traverseStep, array.length]);

  /* reverse: precompute steps as full array snapshots */
  const startReverse = () => {
    const working = [...array];
    const steps = [{ left: 0, right: working.length - 1, snapshot: [...working], swapped: false }];
    let l = 0, r = working.length - 1;
    while (l < r) {
      const tmp = working[l]; working[l] = working[r]; working[r] = tmp;
      steps.push({ left: l, right: r, snapshot: [...working], swapped: true });
      l++; r--;
    }
    steps.push({ left: l, right: r, snapshot: [...working], done: true });
    setReverseSteps(steps);
    setReverseStep(0);
    setMode('reverse');
  };
  const applyReverseResult = () => {
    if (!reverseSteps) return;
    setArray(reverseSteps[reverseSteps.length - 1].snapshot);
    setReverseSteps(null); setReverseStep(0); setMode('build');
  };

  /* search: precompute comparison steps */
  const startSearch = () => {
    if (searchTarget.trim() === '' || isNaN(Number(searchTarget))) return;
    const target = Number(searchTarget);
    const steps = [];
    let foundAt = -1;
    for (let i = 0; i < array.length; i++) {
      const match = array[i] === target;
      steps.push({ i, match });
      if (match) { foundAt = i; break; }
    }
    if (foundAt === -1) steps.push({ i: -1, done: true });
    setSearchSteps(steps);
    setSearchStep(0);
    setMode('search');
  };

  /* quiz */
  const newQuiz = () => {
    const filledIdx = array.map((v, i) => (v !== null ? i : null)).filter(v => v !== null);
    if (filledIdx.length === 0) return;
    const idx = filledIdx[Math.floor(Math.random() * filledIdx.length)];
    setQuizIndex(idx); setQuizAnswer(''); setQuizResult(null);
  };
  const checkQuiz = () => {
    if (quizAnswer.trim() === '' || isNaN(Number(quizAnswer))) return;
    const correct = Number(quizAnswer) === array[quizIndex];
    setQuizResult(correct ? 'correct' : 'wrong');
    setQuizScore(s => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
  };

  /* ── derived render state per mode ─────────────────────────────── */
  let boxes = array, getBoxState = () => ({}), onClickBox = null, code = { lines: [], active: -1 }, codeTitle = 'Java';

  if (mode === 'build') {
    onClickBox = clickBox;
    getBoxState = i => {
      if (i === flashIndex) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff', pop: true, tag: 'updated', tagColor: GREEN };
      if (i === selectedIndex) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tag: `arr[${i}]`, tagColor: V2 };
      if (array[i] === null) return { border: 'var(--border-strong)' };
      return {};
    };
    code = codeFor('build', lastAction);
    codeTitle = 'Java — Build';
  }

  if (mode === 'traverse') {
    getBoxState = i => {
      if (i === traverseStep) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tag: `i = ${i}`, tagColor: V2 };
      if (i < traverseStep) return { bg: 'var(--bg2)', border: 'var(--border)' };
      return {};
    };
    code = codeFor('traverse', { started: traverseStep >= 0 });
    codeTitle = 'Java — Traversal';
  }

  if (mode === 'reverse' && reverseSteps) {
    const cur = reverseSteps[reverseStep];
    boxes = cur.snapshot;
    getBoxState = i => {
      if (cur.done) {
        if (i === cur.left) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff', tag: 'meet', tagColor: GREEN };
        return {};
      }
      if (i === cur.left) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tag: 'left', tagColor: V2 };
      if (i === cur.right) return { bg: `linear-gradient(135deg,${TEAL},#2563EB)`, border: TEAL, color: '#fff', tag: 'right', tagColor: TEAL };
      return {};
    };
    code = codeFor('reverse', { line: reverseStep === 0 ? 0 : (cur.done ? 1 : 4) });
    codeTitle = 'Java — Reverse';
  }

  if (mode === 'search' && searchSteps) {
    const cur = searchSteps[searchStep];
    getBoxState = i => {
      if (cur.done) return {};
      if (i === cur.i) {
        if (cur.match) return { bg: `linear-gradient(135deg,${GREEN},#27AE60)`, border: GREEN, color: '#fff', tag: 'found', tagColor: GREEN };
        return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff', tag: 'checking', tagColor: V2 };
      }
      if (i < cur.i) return { bg: 'var(--bg2)', border: 'var(--border)' };
      return {};
    };
    code = codeFor('search', { line: cur.done ? 5 : (cur.match ? 2 : 1) });
    codeTitle = 'Java — Linear Search';
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => {
              if (m.key === 'reverse') { startReverse(); return; }
              if (m.key === 'search') { setMode('search'); setSearchSteps(null); return; }
              if (m.key === 'quiz') { setMode('quiz'); newQuiz(); return; }
              if (m.key === 'traverse') { setMode('traverse'); setTraverseStep(-1); return; }
              setMode('build');
            }}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
        <div>
          <BoxRow array={boxes} getBoxState={getBoxState} onClickBox={mode === 'build' ? onClickBox : null} />

          {mode === 'build' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 70 }}>Size (3–10)</span>
                <input value={sizeInput} onChange={e => setSizeInput(e.target.value)} style={smallInput} placeholder="6" />
                <button onClick={createArray} style={smallBtn(V1)}>Create Array</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 70 }}>Insert next</span>
                <input value={insertValue} onChange={e => setInsertValue(e.target.value)} style={smallInput} placeholder="value" disabled={isFull} />
                <button onClick={insertNext} style={smallBtn(V1, isFull)} disabled={isFull}>{isFull ? 'Array Full' : 'Insert'}</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 70 }}>Update</span>
                <input value={updateIndex} onChange={e => setUpdateIndex(e.target.value)} style={{ ...smallInput, width: 60 }} placeholder="index" />
                <input value={updateValue} onChange={e => setUpdateValue(e.target.value)} style={smallInput} placeholder="value" />
                <button onClick={applyUpdate} style={smallBtn(TEAL)}>Update</button>
              </div>

              <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>
                Click any box above to see its value read out as arr[i] in the code panel.
              </p>
            </div>
          )}

          {mode === 'traverse' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              <button onClick={() => setTraverseStep(s => Math.max(0, s === -1 ? 0 : s - 1))} disabled={traverseStep <= 0} style={ghostBtn(traverseStep <= 0)}>← Prev</button>
              <button onClick={() => setTraverseStep(s => Math.min(array.length - 1, s + 1))} disabled={traverseStep >= array.length - 1} style={smallBtn(V1, traverseStep >= array.length - 1)}>Next →</button>
              <button onClick={() => setTraversePlaying(p => !p)} style={smallBtn(traversePlaying ? RED : GREEN)}>{traversePlaying ? 'Pause' : 'Auto Play'}</button>
              <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{traverseStep === -1 ? 'Not started' : `i = ${traverseStep}, arr[${traverseStep}] = ${array[traverseStep] === null ? '·' : array[traverseStep]}`}</span>
            </div>
          )}

          {mode === 'reverse' && reverseSteps && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              <button onClick={() => setReverseStep(s => Math.max(0, s - 1))} disabled={reverseStep === 0} style={ghostBtn(reverseStep === 0)}>← Prev</button>
              <button onClick={() => setReverseStep(s => Math.min(reverseSteps.length - 1, s + 1))} disabled={reverseStep === reverseSteps.length - 1} style={smallBtn(V1, reverseStep === reverseSteps.length - 1)}>Next →</button>
              <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {reverseStep + 1} of {reverseSteps.length}</span>
              {reverseSteps[reverseStep].done && <button onClick={applyReverseResult} style={smallBtn(GREEN)}>Apply & Return to Build</button>}
            </div>
          )}

          {mode === 'search' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Target</span>
                <input value={searchTarget} onChange={e => setSearchTarget(e.target.value)} style={smallInput} placeholder="value" />
                <button onClick={startSearch} style={smallBtn(V1)}>Start Search</button>
              </div>
              {searchSteps && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button onClick={() => setSearchStep(s => Math.max(0, s - 1))} disabled={searchStep === 0} style={ghostBtn(searchStep === 0)}>← Prev</button>
                  <button onClick={() => setSearchStep(s => Math.min(searchSteps.length - 1, s + 1))} disabled={searchStep === searchSteps.length - 1} style={smallBtn(V1, searchStep === searchSteps.length - 1)}>Next →</button>
                  <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                    {searchSteps[searchStep].done ? `Target ${searchTarget} was not found` :
                      searchSteps[searchStep].match ? `Found at index ${searchSteps[searchStep].i}` :
                        `Comparing arr[${searchSteps[searchStep].i}] with ${searchTarget}`}
                  </span>
                </div>
              )}
            </div>
          )}

          {mode === 'quiz' && (
            <div>
              {filledCount === 0 ? (
                <p style={{ fontSize: 13.5, color: 'var(--muted)' }}>Build an array with some values first, then come back here to quiz yourself.</p>
              ) : quizIndex !== null && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>What is arr[{quizIndex}]?</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input value={quizAnswer} onChange={e => setQuizAnswer(e.target.value)} style={smallInput} placeholder="your answer" disabled={quizResult !== null} />
                    {quizResult === null ? (
                      <button onClick={checkQuiz} style={smallBtn(V1)}>Check</button>
                    ) : (
                      <button onClick={newQuiz} style={smallBtn(TEAL)}>Next Question</button>
                    )}
                  </div>
                  {quizResult && (
                    <div style={{
                      fontSize: 13, fontWeight: 700, padding: '8px 14px', borderRadius: 9,
                      background: quizResult === 'correct' ? 'rgba(46,204,113,0.12)' : 'rgba(240,65,85,0.1)',
                      color: quizResult === 'correct' ? '#1e9e57' : RED,
                    }}>
                      {quizResult === 'correct' ? 'Correct!' : `Not quite — arr[${quizIndex}] is ${array[quizIndex]}.`}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {quizScore.correct} / {quizScore.total}</div>
                </div>
              )}
            </div>
          )}
        </div>

        <CodePanel title={codeTitle} lines={code.lines} active={code.active} />
      </div>

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default ArrayPlayground;
